import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useToasts } from "../hooks/useToasts";
import Form from "../components/Form";
import { Eye, EyeOff, X } from "lucide-react";

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const { addToast } = useToasts();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const returnTo = searchParams.get('returnTo');
  const lessonId = searchParams.get('lesson');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      addToast(
        "Please accept the Terms of Use and Privacy Policy to continue",
        "error-warning"
      );
      return;
    }
    
    setLoading(true);

    const res = await signup(email, password, passwordConfirm);
    setLoading(false);
    if (res.ok) {
      addToast(
        res.message || "Account created. Please verify your email.",
        "success-info"
      );
      navigate("/email-verification-notice", { state: { email } });
    } else {
      addToast(
        Array.isArray(res.error) ? res.error.join(", ") : res.error,
        "error-warning"
      );
    }
  };

  const handleCancel = () => {
    if (returnTo === 'home' && lessonId) {
      navigate(`/?lesson=${lessonId}`);
    } else {
      navigate("/");
    }
  };

  return (
    <Form
      title="Signup"
      onSubmit={handleSubmit}
      error={null}
      loading={loading}
      submitText="Create Account"
      cancelText="Cancel"
      onCancel={handleCancel}
      containerClassName="signup-form-container"
      footerLinks={
        <p className="signup-footer-login">
          <span className="signup-footer-login-copy">Already have an account?</span>{" "}
          <Link to={returnTo === 'home' && lessonId ? `/login?returnTo=home&lesson=${lessonId}` : "/login"}>Login</Link>
        </p>
      }
    >
      <label htmlFor="email">
        Email
        <div className="login-input-wrapper">
          <input
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={`login-input ${email ? "login-input-clear" : ""}`}
          />
          {email && (
            <button
              type="button"
              aria-label="Clear email"
              tabIndex={-1}
              onClick={() => setEmail("")}
              className="login-input-btn login-input-btn-clear"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </label>
      <label htmlFor="password">
        Password
        <div className="login-input-wrapper">
          <input
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className={`login-input ${password ? "login-input-actions" : ""}`}
          />
          {password && (
            <>
              <button
                type="button"
                aria-label="Clear password"
                tabIndex={-1}
                onClick={() => setPassword("")}
                className="login-input-btn login-input-btn-clear login-input-btn-clear-password"
              >
                <X size={16} />
              </button>
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword(!showPassword)}
                className="login-input-btn login-input-btn-toggle"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </>
          )}
        </div>
      </label>
      <label htmlFor="passwordConfirm">
        Confirm Password
        <div className="login-input-wrapper">
          <input
            type={showPasswordConfirm ? "text" : "password"}
            id="passwordConfirm"
            name="passwordConfirm"
            autoComplete="new-password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
            className={`login-input ${passwordConfirm ? "login-input-actions" : ""}`}
          />
          {passwordConfirm && (
            <>
              <button
                type="button"
                aria-label="Clear password confirmation"
                tabIndex={-1}
                onClick={() => setPasswordConfirm("")}
                className="login-input-btn login-input-btn-clear login-input-btn-clear-password"
              >
                <X size={16} />
              </button>
              <button
                type="button"
                aria-label={
                  showPasswordConfirm
                    ? "Hide password confirmation"
                    : "Show password confirmation"
                }
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                className="login-input-btn login-input-btn-toggle"
              >
                {showPasswordConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </>
          )}
        </div>
      </label>
      <label className="signup-legal">
        <input
          type="checkbox"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
          required
          className="signup-legal-checkbox"
        />
        <span className="signup-legal-copy">
          I have read and agree to the{" "}
          <Link to="/terms" target="_blank" rel="noopener noreferrer">
            Terms of Use
          </Link>{" "}
          and{" "}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link>
        </span>
      </label>
    </Form>
  );
};

export default Signup;
